import { CheckIndexedDBIsSupport } from "@/IndexedDB/tabIndexedDB";
import { CheckBroadcastIsSupport } from "@/broadcastChannel/tabChannel";
import {
  BroadcastAuthenticatedNotificationStatus,
  BroadcastIsUpdateNotificationCount,
  BroadcastIsUpdateNotificationList,
} from "@/broadcastChannel/userChannel";
import { AuthenticatedNotification, LoadingStatusType, TypeComponentNoti } from "@/enums/enums";
import {
  ClearTimerCheckAccessToken,
  DoAddNotificationCount,
  DoAddReadNotificationList,
  DoAddUnreadNotificationList,
  DoDeleteNotificationByIdFromEmit,
  DoGetNotificationCount,
  DoGetReadAllNotificationList,
  DoGetUnreadAllNotificationList,
  SetIsSeenForNotification,
  SetTimerCheckAccessToken,
  SocketAuthentication,
  SortDataNotification,
} from "@/helper/notificationHelper";
import { MerchantId } from "@/helper/userHelper";
import { i18n } from "@/i18n";
import { NotificationMessageModel } from "@/model/notification/NotificationMessageModel";
import API from "@/service/Index";
import store from "@/store/index";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

@Module({ namespaced: true })
export default class Notification extends VuexModule {
  public notificationIsConnect = false;
  public notificationReadyToUse = false;
  public notificationIsAuthenticated = false;
  public notificationList: Array<NotificationMessageModel> = [];
  public notificationCount = 0;
  public notificationListLoadingStatus: LoadingStatusType = LoadingStatusType.None;
  public notificationCountLoadingStatus: LoadingStatusType = LoadingStatusType.None;

  get getNotificationList(): Array<NotificationMessageModel> {
    return this.notificationList;
  }

  get getNotificationCount(): number {
    return this.notificationCount;
  }

  get getNotificationIsConnect(): boolean {
    return this.notificationIsConnect;
  }

  get getNotificationReadyToUse(): boolean {
    return this.notificationReadyToUse;
  }

  get getNotificationIsAuthenticated(): boolean {
    return this.notificationIsAuthenticated;
  }

  get getNotificationListLoadingStatus(): LoadingStatusType {
    return this.notificationListLoadingStatus;
  }

  get getNotificationCountLoadingStatus(): LoadingStatusType {
    return this.notificationCountLoadingStatus;
  }

  @Mutation
  public MutationSetNotificationListLoadingStatus(status: LoadingStatusType) {
    this.notificationListLoadingStatus = status;
  }

  @Mutation
  public MutationSetNotificationCountLoadingStatus(status: LoadingStatusType) {
    this.notificationCountLoadingStatus = status;
  }

  /* eslint-disable @typescript-eslint/camelcase */
  @Mutation
  public SOCKET_authenticated(payload: any) {
    if (payload) {
      this.notificationIsAuthenticated = true;
      this.notificationReadyToUse = true;
      SetTimerCheckAccessToken();
      BroadcastAuthenticatedNotificationStatus(AuthenticatedNotification.Success);
    } else {
      this.notificationReadyToUse = false;
      BroadcastAuthenticatedNotificationStatus(AuthenticatedNotification.Failed);
    }
  }
  /* eslint-disable @typescript-eslint/camelcase */
  @Mutation
  public SOCKET_connect() {
    this.notificationIsConnect = true;
    SocketAuthentication();
  }
  /* eslint-disable @typescript-eslint/camelcase */
  @Mutation
  public SOCKET_disconnect() {
    this.notificationReadyToUse = false;
    this.notificationIsConnect = false;
    this.notificationIsAuthenticated = false;
    ClearTimerCheckAccessToken();
  }
  /* eslint-disable @typescript-eslint/camelcase */
  @Mutation
  public async SOCKET_GetNotificationCount(payload: any) {
    store.dispatch("Notification/ActionSOCKETGetNotificationCount", payload, { root: true });
  }

  @Mutation
  public async SOCKET_RemoveNotificationById(payload: any) {
    store.dispatch("Notification/ActionSOCKETRemoveNotificationById", payload, { root: true });
  }

  @Mutation
  public MutationSetNotificationReadyToUse(status: boolean) {
    this.notificationReadyToUse = status;
  }

  @Mutation
  public async MutationSetNotificationCount(count: number) {
    this.notificationCount = count;
  }

  @Mutation
  public async MutationSetNotificationList(notificationList: Array<NotificationMessageModel>) {
    this.notificationList = notificationList;
  }

  @Action
  public async ActionSOCKETGetNotificationCount(payload: any) {
    this.context.commit("MutationSetNotificationCountLoadingStatus", LoadingStatusType.Loading);
    const merchantId = MerchantId();
    if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
      await DoAddNotificationCount(payload.merchantId, payload.notificationCount);
      if (payload.merchantId == merchantId) {
        const count = await DoGetNotificationCount(merchantId);
        this.context.commit("MutationSetNotificationCount", count);
      }
      BroadcastIsUpdateNotificationCount(payload.merchantId);
    } else {
      this.context.commit("MutationSetNotificationCount", payload.notificationCount);
    }
    setTimeout(() => {
      this.context.commit("MutationSetNotificationCountLoadingStatus", LoadingStatusType.Success);
    }, 0);
  }

  @Action
  public async ActionSOCKETRemoveNotificationById(payload: any) {
    if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
      DoDeleteNotificationByIdFromEmit(payload.merchantId, payload.notificationId);
    } else {
      const notificationList = this.notificationList.filter((x) => x.id !== payload.notificationId);
      this.context.commit("MutationSetNotificationList", notificationList);
    }
  }

  @Action
  public async ActionSetNotificationListFromIndexedDB() {
    this.context.commit("MutationSetNotificationListLoadingStatus", LoadingStatusType.Loading);
    const merchantId = MerchantId();
    const unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
    const readNotificationList = await DoGetReadAllNotificationList(merchantId);
    const notificationList = [
      ...SetIsSeenForNotification(unreadNotificationList, false),
      ...SetIsSeenForNotification(readNotificationList, true),
    ];

    this.context.commit("MutationSetNotificationList", SortDataNotification(notificationList));

    setTimeout(() => {
      this.context.commit("MutationSetNotificationListLoadingStatus", LoadingStatusType.Success);
    }, 0);
  }

  @Action
  public async ActionSetNotificationCountFromIndexedDB() {
    this.context.commit("MutationSetNotificationCountLoadingStatus", LoadingStatusType.Loading);
    const merchantId = MerchantId();
    const count = await DoGetNotificationCount(merchantId);
    this.context.commit("MutationSetNotificationCount", count);
    setTimeout(() => {
      this.context.commit("MutationSetNotificationCountLoadingStatus", LoadingStatusType.Success);
    }, 0);
  }

  @Action
  public DoGetUnseenNotificationCount(notificationMerchantId: number) {
    this.context.commit("MutationSetNotificationCountLoadingStatus", LoadingStatusType.Loading);
    API.NotificationService.DoGetUnseenNotificationCount(notificationMerchantId).then(async (res) => {
      if (res.status.IsSuccess()) {
        const merchantId = MerchantId();
        const notificationCount = res.data.notificationCount;

        if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
          await DoAddNotificationCount(notificationMerchantId, notificationCount);
          if (notificationMerchantId == merchantId) {
            const count = await DoGetNotificationCount(merchantId);
            this.context.commit("MutationSetNotificationCount", count);
          }
          BroadcastIsUpdateNotificationCount(notificationMerchantId);
        } else {
          this.context.commit("MutationSetNotificationCount", notificationCount);
        }
      }
      this.context.commit("MutationSetNotificationCountLoadingStatus", LoadingStatusType.Success);
    });
  }

  @Action
  public DoGetAllNotificationList(notificationMerchantId: number) {
    const doGetUnseenNotificationList = API.NotificationService.DoGetUnseenNotificationList(
      notificationMerchantId
    ).then((res) => {
      if (res.status.IsSuccess()) {
        const unreadNotificationList = [...res.data.list];
        return unreadNotificationList;
      }
    });

    const doGetSeenNotificationList = API.NotificationService.DoGetSeenNotificationList(notificationMerchantId).then(
      (res) => {
        if (res.status.IsSuccess()) {
          const readNotificationList = [...res.data.list];
          return readNotificationList;
        }
      }
    );

    this.context.commit("MutationSetNotificationListLoadingStatus", LoadingStatusType.Loading);

    Promise.all([doGetUnseenNotificationList, doGetSeenNotificationList]).then(async (results) => {
      const unreadNotificationListFromAPI: any = results[0];
      const readNotificationListFromAPI: any = results[1];
      const merchantId = MerchantId();

      if (CheckBroadcastIsSupport() && CheckIndexedDBIsSupport()) {
        await DoAddUnreadNotificationList(notificationMerchantId, unreadNotificationListFromAPI);
        await DoAddReadNotificationList(notificationMerchantId, readNotificationListFromAPI);
        if (notificationMerchantId == merchantId) {
          const unreadNotificationList = await DoGetUnreadAllNotificationList(merchantId);
          const readNotificationList = await DoGetReadAllNotificationList(merchantId);
          const notificationList = [
            ...SetIsSeenForNotification(unreadNotificationList, false),
            ...SetIsSeenForNotification(readNotificationList, true),
          ];

          this.context.commit("MutationSetNotificationList", SortDataNotification(notificationList));
        }
        BroadcastIsUpdateNotificationList(notificationMerchantId);
      } else {
        const notificationList = [
          ...SetIsSeenForNotification(unreadNotificationListFromAPI, false),
          ...SetIsSeenForNotification(readNotificationListFromAPI, true),
        ];
        this.context.commit("MutationSetNotificationList", SortDataNotification(notificationList));
      }

      this.context.commit("MutationSetNotificationListLoadingStatus", LoadingStatusType.Success);
    });
  }

  @Action
  public DoDeleteUnseenNotification(input: { merchantId: number; notificationId: string }) {
    API.NotificationService.DoDeleteUnseenNotification(input.merchantId, input.notificationId).then(async (res) => {
      if (!res.status.IsSuccess()) {
        store.dispatch(
          "Alert/SetAlertMessage",
          {
            alertMessage: i18n.t("Something_Wrong"),
            typeMessage: TypeComponentNoti.Error,
          },
          { root: true }
        );
      }
    });
  }

  @Action
  public DoDeleteSeenNotification(input: { merchantId: number; notificationId: string }) {
    API.NotificationService.DoDeleteSeenNotification(input.merchantId, input.notificationId).then(async (res) => {
      if (!res.status.IsSuccess()) {
        store.dispatch(
          "Alert/SetAlertMessage",
          {
            alertMessage: i18n.t("Something_Wrong"),
            typeMessage: TypeComponentNoti.Error,
          },
          { root: true }
        );
      }
    });
  }

  @Action
  public DoSetAllNotificationIsSeen(merchantId: number) {
    API.NotificationService.DoSetAllNotificationIsSeen(merchantId).then(async (res) => {
      if (!res.status.IsSuccess()) {
        store.dispatch(
          "Alert/SetAlertMessage",
          {
            alertMessage: i18n.t("Something_Wrong"),
            typeMessage: TypeComponentNoti.Error,
          },
          { root: true }
        );
      }
    });
  }
}
